import React, { useEffect } from "react";
import { NavigateFunction } from "react-router-dom";
import { observer } from "mobx-react";
import { debounce } from "lodash";
import {
  AccountSettingsStore,
  ItemsStore,
  RolesAndPermissionStore,
} from "../store";
import {
  AuthService,
  ITableHeaders,
  TableHeader,
  ToastService,
  UtilityMethods,
  withRouter,
} from "@mindmemobile/mm-shared-global";
import {  DonationService } from "../services";
import {
  downloadBlob,
  exportFileName,
  isUsableExportFile,
  showExportError,
  ExportFormat,
} from "../utils";
import ExportDropDown from "./ExportDropDown";
import TableGrid from "./TableGrid";
import { GridNames } from "../enums";
import DashboardTopRightSide from "./DashboardTopRightSide";
import DashboardTopBar from "./DashboardTopBar";
import ItemContentArea from "./ItemContentArea";
import { DonarsGridModel } from "../models";
import { MerchantAccountStore } from "../../modules";

type IProps = {
  navigate: NavigateFunction;
  itemStore: ItemsStore;
  rolesAndPermissionStore: RolesAndPermissionStore;
  merchantAccountStore: MerchantAccountStore;
};
const DonorsGrid: React.FC<IProps> = ({
  itemStore,
  navigate,
  rolesAndPermissionStore,
  merchantAccountStore,
}) => {
  const [showLoader, setShowLoader] = React.useState(true);
  const [firstTimeLoad, setFirstTimeLoad] = React.useState(true);
  const [isExporting, setIsExporting] = React.useState(false);

  /* Exports every donor, not just the page on screen: the API builds the file from the same query
     the grid uses, minus pagination, honouring the active search. */
  function exportAll(format: ExportFormat) {
    if (isExporting) return;
    setIsExporting(true);
    DonationService.exportDonors(
      AuthService.authData.accountId,
      format,
      itemStore.searchValue
    ).subscribe({
      next: (blob) => {
        // Cleared before the download so a browser that refuses it cannot strand the button.
        setIsExporting(false);
        if (!isUsableExportFile(blob)) {
          ToastService.showError("Error in exporting donors");
          return;
        }
        downloadBlob(blob, exportFileName("donors", format));
      },
      error: (error) => {
        setIsExporting(false);
        showExportError(error, "Error in exporting donors");
      },
    });
  }

  useEffect(() => {
    loadData(
      itemStore.donarGridPagination.pageNumber,
      itemStore.donarGridPagination.pageSize,
      itemStore.searchValue,
      true
    );
    itemStore.searchSubject.subscribe(() => {
      debounceSearch();
    });
  }, []);

  function loadData(
    pageNumber: number,
    pageSize: number,
    searchValue: string,
    firstTimeLoad: boolean = false
  ) {
    setFirstTimeLoad(firstTimeLoad);
    setShowLoader(true);
    DonationService.getDonars(
      AuthService.authData.accountId,
      pageNumber,
      pageSize,
      searchValue
    ).subscribe({
      next: (response) => {
        setShowLoader(false);
        itemStore.deserializeDonarsGridData(
          response.result || [],
          response.meta
        );
      },
    });
  }

  const debounceSearch = debounce(
    () =>
      loadData(
        itemStore.donarGridPagination.pageNumber,
        itemStore.donarGridPagination.pageSize,
        itemStore.searchValue
      ),
    500
  );

  function onPaginationChange(pageNumber: number, pageSize: number) {
    loadData(pageNumber, pageSize, itemStore.searchValue);
  }

  function goToDetailPage(id: string) {
    if (!id) return;
    navigate(`/app/contacts/detail/${id}`);
  }

  const isNoData =
    firstTimeLoad &&
    itemStore.donationGridPagination.totalItemCount === 0 &&
    !showLoader &&
    itemStore.searchValue === "";

  function renderDetails(node: React.ReactNode, showItemRightContent = true) {
    const content = showItemRightContent ? (
      <DashboardTopRightSide
        itemStore={itemStore}
        showSearchInput={!isNoData}
        showButton={!isNoData}
        extraContent={
          !isNoData && (
            <ExportDropDown onExport={exportAll} isExporting={isExporting} />
          )
        }
      />
    ) : (
      <></>
    );
    return (
      <>
        <DashboardTopBar
          itemStore={itemStore}
          rightContent={<>{showLoader ? null : content}</>}
          rolesAndPermissionStore={rolesAndPermissionStore}
          merchantAccountStore={merchantAccountStore}
        />
        <ItemContentArea
          itemStore={itemStore}
          node={node}
          showLoader={showLoader}
          // className={isNoData ? "content-area-only" : ""}
          showMerchantAccountNotificationBar={merchantAccountStore.hasNotificationBar()}
        />
      </>
    );
  }

  function columns() {
    const columns: ITableHeaders<DonarsGridModel>[] = [
      {
        dataKey: "contact",
        title: "Contact",
        cellColor: "#556bb8",
        onCellRender: (data: DonarsGridModel) => {
          return data.contact?.fullName || data.contact?.email || "";
        },
        onCellClick: (
          e: React.MouseEvent<HTMLTableDataCellElement, MouseEvent>,
          data: DonarsGridModel
        ) => {
          goToDetailPage(data.contact?.id || "");
        },
      },
      /* The money and count columns are right-aligned, matching every other numeric column in the
         app, so the figures line up under their headers instead of ragging off the left edge. */
      {
        dataKey: "totalAmount",
        title: "Total Donations",
        cellClassName: "table-col-right",
        className: "table-col-right",
        onCellRender: (data: DonarsGridModel) => {
          return "$" + AccountSettingsStore.formatAmount(data.totalAmount);
        },
      },
      {
        dataKey: "totalNumber",
        title: "Total Number",
        cellClassName: "table-col-right",
        className: "table-col-right",
        onCellRender: (data: DonarsGridModel) => {
          return UtilityMethods.numberWithCommas(data.totalNumber);
        },
      },
      {
        dataKey: "lastAmount",
        title: "Last Donation",
        cellClassName: "table-col-right",
        className: "table-col-right",
        onCellRender: (data: DonarsGridModel) => {
          return "$" + AccountSettingsStore.formatAmount(data.lastAmount);
        },
      },
      {
        dataKey: "lastDonatedOn",
        title: "Last Donation Date",
        onCellRender: (data: DonarsGridModel) => {
          return UtilityMethods.formatDateInString(data.lastDonatedOn);
        },
      },
      {
        dataKey: "isRecurring",
        title: "Recurring",
        onCellRender: (data: DonarsGridModel) => {
          return data.isRecurring ? "Yes" : "No";
        },
      },
    ];
    return columns;
  }

  return renderDetails(
    <TableGrid
      tableHeader={
        <TableHeader
          showFilter={itemStore.showItemsFilter}
          onToggleFilter={() => itemStore.toggleItemsFilter()}
          total={itemStore.donarGridPagination.totalItemCount}
        />
      }
      countriesWithState={itemStore.countryStore.countriesWithState}
      totalItemCount={itemStore.donarGridPagination.totalItemCount}
      cellSpacing={20}
      onPaginationChange={(pageNumber: number, pageSize: number) =>
        onPaginationChange(pageNumber, pageSize)
      }
      pagination={itemStore.donarGridPagination}
      data={itemStore.donarsGridData}
      headers={columns()}
      name={GridNames.Donors}
      hideTable={isNoData}
      height={
        merchantAccountStore.hasNotificationBar() ? "calc(100vh - 350px)" : ""
      }
    />
  );
};
export default withRouter(observer(DonorsGrid));
